import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { restoreCsrTheme } from '../../csr/csr-theme';
import { AgentPerformanceItem, SupervisorService, AgentStatus } from '../../../core/services/supervisor.service';

interface WallboardAgent {
  name: string;
  initials: string;
  ext: string;
  task: string;
  status: 'On Call' | 'Available' | 'Away' | 'Offline' | 'Unrouted';
  callTime: string;
  totalCalls: number | string;
  handled: number | string;
  missed: number | string;
  unrouted: number | string;
  avg: string;
  avgHold: string;
  fcr: string;
  callbacks: number;
  wrapUp: string;
  slaOk: boolean;
  gradFrom: string;
  gradTo: string;
}

@Component({
  selector: 'app-supervisor-agent-wallboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './supervisor-agent-wallboard.component.html',
  styleUrls: ['./supervisor-agent-wallboard.component.scss']
})
export class SupervisorAgentWallboardComponent implements OnInit, OnDestroy {
  private subs = new Subscription();
  private pollInterval?: ReturnType<typeof setInterval>;
  private liveAgents: AgentStatus[] = [];
  private performance: AgentPerformanceItem[] = [];
  fromDate = this.today();
  toDate = this.today();

  stats: Array<{ label: string; val: string; col: string }> = [
    { label: 'On Call',          val: '-', col: 'blue'   },
    { label: 'Available',        val: '-', col: 'green'  },
    { label: 'Break / Away',     val: '0', col: 'amber'  },
    { label: 'Offline',          val: '0', col: 'red'    },
    { label: 'Avg Handle',       val: '-', col: 'cyan'   },
    { label: 'Total Calls',      val: '-', col: 'purple' },
  ];

  agents: WallboardAgent[] = [];

  constructor(private supervisor: SupervisorService) {}

  ngOnInit(): void {
    restoreCsrTheme();

    this.supervisor.fetchAgents();
    this.supervisor.fetchStats();
    this.fetchPerformance();

    this.pollInterval = setInterval(() => {
      this.supervisor.fetchAgents();
      this.supervisor.fetchStats();
      this.fetchPerformance();
    }, 15000);

    this.subs.add(this.supervisor.agents$.subscribe(list => {
      this.liveAgents = list.filter(agent => this.isWallboardAgent(agent));
      this.refreshAgents();
      const onCall = this.liveAgents.filter(a => a.status === 'ON_CALL').length;
      const available = this.liveAgents.filter(a => a.status === 'AVAILABLE').length;
      this.stats[0].val = String(onCall);
      this.stats[1].val = String(available);
    }));

    this.subs.add(this.supervisor.stats$.subscribe(s => {
      if (!s) return;
      this.stats[4].val = this.fmtSecs(s.avgHandleSeconds);
    }));

    this.subs.add(this.supervisor.agentPerformance$.subscribe(report => {
      this.performance = report?.agents ?? [];
      this.refreshAgents();
      this.stats[5].val = String(this.performance.reduce((sum, agent) => sum + agent.totalCalls, 0));
    }));
  }

  ngOnDestroy(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.subs.unsubscribe();
  }

  downloadCsv(): void {
    window.open(this.supervisor.reportExportUrl('agents', this.fromDate, this.toDate), '_blank');
  }

  fetchPerformance(): void {
    this.supervisor.fetchAgentPerformance(this.fromDate, this.toDate);
  }

  agentBadge(status: string): string {
    return status === 'On Call' ? 'sb-blue' : status === 'Available' ? 'sb-green'
         : status === 'Away'    ? 'sb-amber' : status === 'Unrouted' ? 'sb-red' : 'sb-gray';
  }

  fcrColor(fcr: string): string {
    const n = parseInt(fcr, 10);
    if (isNaN(n)) return 'var(--csr-text2)';
    return n > 80 ? '#66bb6a' : n > 60 ? '#ffa726' : '#ef5350';
  }

  wrapUpColor(wrapUp: string): string {
    const n = parseInt(wrapUp, 10);
    if (isNaN(n)) return 'var(--csr-text2)';
    return n >= 95 ? '#66bb6a' : n >= 75 ? '#ffa726' : '#ef5350';
  }

  private readonly gradients = [
    ['#4f8ef7', '#8b5cf6'], ['#00c48c', '#4f8ef7'], ['#ffb020', '#ff5b5b'],
    ['#8b5cf6', '#ec4899'], ['#00d4ff', '#00c48c'], ['#ff5b5b', '#ffb020'],
    ['#4f8ef7', '#00d4ff'], ['#7b8fa6', '#4a5568'],
  ] as const;

  private refreshAgents(): void {
    const liveRows = this.liveAgents.map(a => this.mapAgent(a, this.findPerformance(a.sipExtension)));
    const liveExts = new Set(this.liveAgents.map(a => a.sipExtension));
    const reportOnlyRows = this.performance
      .filter(p => !liveExts.has(p.agentExtension))
      .map(p => this.mapReportOnlyAgent(p));
    this.agents = [...liveRows, ...reportOnlyRows];
  }

  private findPerformance(extension: string): AgentPerformanceItem | undefined {
    return this.performance.find(p => p.agentExtension === extension);
  }

  private isWallboardAgent(agent: AgentStatus): boolean {
    if (agent.role === 'PUBLIC_USER') return false;
    return !/^3\d{3}$/.test(agent.sipExtension || '');
  }

  private mapAgent(a: AgentStatus, perf?: AgentPerformanceItem): WallboardAgent {
    const parts = (a.fullName || perf?.agentName || a.sipExtension).trim().split(/\s+/);
    const initials = parts.slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase();
    const gradIdx = parseInt(a.sipExtension.replace(/\D/g, '') || '0', 10) % this.gradients.length;
    const [gradFrom, gradTo] = this.gradients[gradIdx];
    return {
      name: a.fullName,
      initials,
      ext: a.sipExtension,
      task: perf?.taskLabel || 'Agent calls',
      status: a.status === 'ON_CALL' ? 'On Call' : 'Available',
      callTime: '-',
      totalCalls: perf?.totalCalls ?? '-',
      handled: perf?.handledCalls ?? perf?.totalCalls ?? '-',
      missed: perf?.missedCalls ?? 0,
      unrouted: perf?.unroutedCalls ?? 0,
      avg: perf ? this.fmtSecs(perf.avgHandleSeconds) : '-',
      avgHold: perf ? this.fmtSecs(perf.avgHoldSeconds) : '-',
      fcr: perf ? `${perf.firstCallResolutionPct}%` : 'N/A',
      callbacks: perf?.callbackCount ?? 0,
      wrapUp: perf ? `${perf.wrapUpCompletionPct}%` : 'N/A',
      slaOk: perf?.slaOk ?? true,
      gradFrom,
      gradTo,
    };
  }

  private mapReportOnlyAgent(perf: AgentPerformanceItem): WallboardAgent {
    const isUnrouted = perf.rowType === 'UNROUTED' || perf.agentExtension === 'Unassigned';
    const displayName = isUnrouted ? 'Unrouted Calls' : (perf.agentName || perf.agentExtension);
    const displayExt = isUnrouted ? '-' : perf.agentExtension;
    const parts = displayName.trim().split(/\s+/);
    const initials = parts.slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase();
    const gradIdx = parseInt(perf.agentExtension.replace(/\D/g, '') || '0', 10) % this.gradients.length;
    const [gradFrom, gradTo] = this.gradients[gradIdx];
    return {
      name: displayName,
      initials,
      ext: displayExt,
      task: perf.taskLabel || (isUnrouted ? 'Live-agent handoff' : 'Agent calls'),
      status: isUnrouted ? 'Unrouted' : 'Offline',
      callTime: '-',
      totalCalls: perf.totalCalls,
      handled: perf.handledCalls ?? perf.totalCalls,
      missed: perf.missedCalls ?? 0,
      unrouted: perf.unroutedCalls ?? 0,
      avg: isUnrouted ? '-' : this.fmtSecs(perf.avgHandleSeconds),
      avgHold: isUnrouted ? '-' : this.fmtSecs(perf.avgHoldSeconds),
      fcr: isUnrouted ? 'N/A' : `${perf.firstCallResolutionPct}%`,
      callbacks: perf.callbackCount,
      wrapUp: isUnrouted ? 'N/A' : `${perf.wrapUpCompletionPct}%`,
      slaOk: perf.slaOk ?? true,
      gradFrom,
      gradTo,
    };
  }

  private fmtSecs(seconds: number): string {
    if (!seconds || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }
}
